// Libs
import React, { useEffect, useMemo, FC } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { clientActions } from 'core/client/clientReducers'
import { makeStyles } from '@material-ui/styles'
import Text from 'core/elements/Text'
import useReactRouter from 'use-react-router'

// Components
import DocumentMeta from 'core/components/DocumentMeta'
// Models
import { ErrorMessageCodes, INodesSelector } from './model'
import Progress from 'core/components/progress/Progress'
import UsageWidget from 'core/components/widgets/UsageWidget'
import calcUsageTotalByPath from 'k8s/util/calcUsageTotals'
import HelpContainer from 'core/components/HelpContainer'
import Theme from 'core/themes/model'
import NodeInfo from './NodeInfo'
import { HeaderCard } from '../clusters/ClusterDetailsPage'
import ClusterStatusSpan from '../clusters/ClusterStatusSpan'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import CodeBlock from 'core/components/CodeBlock'
import CopyToClipboard from 'core/components/CopyToClipboard'
import {
  getErrorMessage,
  hasClockDrift,
  meetsHardwareRequirement,
  getAvailableSpace,
} from './helpers'
import { hexToRgbaCss } from 'core/utils/colorHelpers'
import { ClusterSelector } from '../clusters/model'
import {
  ClusterType,
  HardwareType,
  minAvailableDiskSpace,
  nodeHardwareRequirements,
} from '../clusters/bareos/constants'
import useListAction from 'core/hooks/useListAction'
import { listNodes } from './new-actions'
import { listClusters } from '../clusters/newActions'
import { nodesSelector } from 'app/plugins/infrastructure/components/nodes/selectors'
import { clustersSelector } from '../clusters/selectors'
import { capiHostsSelector } from '../clusters/capi/details/overview/hosts/selectors'

// Styles
const useStyles = makeStyles((theme: Theme) => ({
  pageContainer: {
    position: 'relative',
    maxWidth: 1234,
  },
  backLink: {
    position: 'absolute',
    right: 0,
    top: 8,
    zIndex: 100,
    ...theme.typography.caption2,
  },
  nodeInfoContainer: {
    marginTop: theme.spacing(2),
  },
  detailsHeader: {
    display: 'grid',
    gridTemplateColumns: 'max-content repeat(3, max-content)',
    gridGap: theme.spacing(2),
    paddingBottom: 20,
  },
  rowHelp: {
    width: 24,
  },
  rowHeader: {
    display: 'flex',
    justifyContent: 'flex-end',
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  rowValue: {
    marginLeft: theme.spacing(0.5),
    wordBreak: 'break-all',
    display: 'flex',
    flexDirection: 'column',
  },
  clockDriftErrorMessage: {
    maxHeight: 165,
    fontSize: 14,
    padding: theme.spacing(0, 2),
    margin: 0,
    marginTop: theme.spacing(2),
    backgroundColor: 'transparent',
    color: theme.palette.red[500],
    fontWeight: 300,
    lineHeight: '15px',
  },
  clockDriftErrorAlert: {
    maxHeight: 224,
    maxWidth: 'inherit',
    padding: theme.spacing(0, 2, 2, 2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.red[500]}`,
    backgroundColor: hexToRgbaCss(theme.palette.red[500], 0.1),
    borderRadius: 4,

    '& > header': {
      color: theme.palette.red[500],
      borderBottom: `1px solid ${theme.palette.red[500]}`,
      display: 'grid',
      gridGap: theme.spacing(),
      gridTemplateColumns: '22px 1fr 22px',
      fontSize: 14,
      height: 40,
      alignItems: 'center',
    },
  },
  headerCardBody: {
    display: 'grid',
    gap: theme.spacing(1),
    marginTop: 4,
  },
  statusContainer: {
    '& > div': {
      width: '100%',
    },
  },
  status: {
    display: 'flex',
    justifyContent: 'space-between',
    minWidth: '300px',
  },
}))

const NodeDetailsPage: FC = () => {
  const { match } = useReactRouter()
  const classes = useStyles({})
  const dispatch = useDispatch()
  const id = match.params.id

  const { message, loading: loadingNodes } = useListAction(listNodes)
  const { loading: loadingClusters } = useListAction(listClusters)

  const nodes = useSelector(nodesSelector)
  const hosts = useSelector(capiHostsSelector)
  const clusters = useSelector(clustersSelector)

  const data = useMemo(() => [...nodes, ...hosts], [nodes, hosts])
  const selectedNode = useMemo(() => data.find((x) => x.uuid === id), [data, id, hosts])

  useEffect(() => {
    dispatch(
      clientActions.updateBreadcrumbParams({
        id: selectedNode?.name || id,
      }),
    )
    return () => {
      dispatch(clientActions.resetBreadcrumbParams())
    }
  }, [selectedNode?.name, id])

  const cluster: ClusterSelector = selectedNode?.clusterId
    ? clusters.find((cluster) => cluster.uuid === selectedNode?.clusterId)
    : ({} as ClusterSelector)
  const loadingSomething = loadingNodes || loadingClusters
  const clockDriftErrorMsg = getErrorMessage(selectedNode, 'warn', ErrorMessageCodes.timeDrift)

  if (!selectedNode || loadingSomething) {
    return <Progress loading message="Loading Nodes..." />
  }

  return (
    <>
      <DocumentMeta title={`Node - ${selectedNode?.name}`} breadcrumbs />
      <div className={classes.nodeInfoContainer}>
        <NodeStatusAndUsage node={selectedNode} cluster={cluster} />
        {!!clockDriftErrorMsg && <NodeClockDriftError errorMessage={clockDriftErrorMsg} />}
        <NodeInfo />
      </div>
    </>
  )
}

const NodeClockDriftError = ({ errorMessage }) => {
  const classes = useStyles()
  return (
    <div className={classes.clockDriftErrorAlert}>
      <header>
        <FontAwesomeIcon>exclamation-circle</FontAwesomeIcon>
        <Text variant="caption1">Clock Drift Detected</Text>
        <CopyToClipboard copyText={errorMessage} copyIcon="copy" codeBlock={false} />
      </header>
      <CodeBlock className={classes.clockDriftErrorMessage}>{errorMessage}</CodeBlock>
    </div>
  )
}

export const DetailRow: FC<{
  label: string
  value: string | React.ReactNode
  helpMessage?: string
}> = ({ label, value, helpMessage }) => {
  const { rowHeader, rowValue, rowHelp } = useStyles({})
  return (
    <tr>
      <td>
        <Text className={rowHeader} variant="caption1" component="span">
          {label}:
        </Text>
      </td>
      <td>
        <Text
          className={rowValue}
          variant="body2"
          component={typeof value === 'string' ? 'span' : 'div'}
        >
          {value}
        </Text>
      </td>
      <td className={rowHelp}>
        {!!helpMessage && <HelpContainer title={helpMessage} color="black" />}
      </td>
    </tr>
  )
}

const NodeStatusAndUsage = ({ node, cluster }) => {
  const classes = useStyles()
  const usage = useMemo(
    () => ({
      compute: calcUsageTotalByPath(
        [node],
        'combined.usage.compute.current',
        'combined.usage.compute.max',
      ),
      memory: calcUsageTotalByPath(
        [node],
        'combined.usage.memory.current',
        'combined.usage.memory.max',
      ),
      disk: calcUsageTotalByPath([node], 'combined.usage.disk.current', 'combined.usage.disk.max'),
    }),
    [node],
  )
  return (
    <div className={classes.detailsHeader}>
      <NodeStatus node={node} cluster={cluster} />
      <UsageWidget title="CPU" stats={usage.compute} units="GHz" />
      <UsageWidget title="Storage" stats={usage.disk} units="GiB" />
      <UsageWidget title="Memory" stats={usage.memory} units="GiB" />
    </div>
  )
}

const NodeStatus = ({ node, cluster }) => {
  const classes = useStyles()
  const clusterType =
    cluster?.nodes?.length === 1 ? ClusterType.SingleNodeCluster : ClusterType.MultiNodeCluster
  const totalRamCapacity = node?.combined?.usage?.memory?.max
  const totalDiskSpace = node?.combined?.usage?.disk?.max
  const availableDiskSpace = getAvailableSpace(node?.combined?.usage?.disk)
  const nodeHasClockDrift = hasClockDrift(node)

  return (
    <HeaderCard title={node?.name} subtitle="">
      <div className={classes.headerCardBody}>
        <ClusterStatusSpan
          rootClassName={classes.statusContainer}
          className={classes.status}
          title={`Recommended Minimum RAM Capacity: ${
            nodeHardwareRequirements[clusterType][HardwareType.RAM]
          } GB`}
          status={
            meetsHardwareRequirement(totalRamCapacity, clusterType, HardwareType.RAM)
              ? 'ok'
              : 'fail'
          }
          variant="header"
          iconStatus
          iconPosition="right"
        >
          {`RAM Capacity: ${totalRamCapacity?.toFixed(2)} GB`}
        </ClusterStatusSpan>
        <ClusterStatusSpan
          rootClassName={classes.statusContainer}
          className={classes.status}
          title={`Recommended Minimum Available Disk Space: ${minAvailableDiskSpace} GB`}
          status={availableDiskSpace >= minAvailableDiskSpace ? 'ok' : 'fail'}
          variant="header"
          iconStatus
          iconPosition="right"
        >
          {`Available Disk Space: ${availableDiskSpace?.toFixed(2)} GB`}
        </ClusterStatusSpan>
        <ClusterStatusSpan
          rootClassName={classes.statusContainer}
          className={classes.status}
          title={`Recommended Minimum Total Disk Space: ${
            nodeHardwareRequirements[clusterType][HardwareType.Disk]
          } GB`}
          status={
            meetsHardwareRequirement(totalDiskSpace, clusterType, HardwareType.Disk) ? 'ok' : 'fail'
          }
          variant="header"
          iconStatus
          iconPosition="right"
        >
          {`Total Disk Space: ${totalDiskSpace?.toFixed(2)} GB`}
        </ClusterStatusSpan>
        <ClusterStatusSpan
          rootClassName={classes.statusContainer}
          className={classes.status}
          title={`Host clock ${nodeHasClockDrift ? 'is' : 'is not'} out of sync`}
          status={nodeHasClockDrift ? 'error' : 'ok'}
          variant="header"
          iconStatus
          iconPosition="right"
        >
          {nodeHasClockDrift ? 'Clock Drift Detected' : 'No Clock Drift Detected'}
        </ClusterStatusSpan>
      </div>
    </HeaderCard>
  )
}

export default NodeDetailsPage
